import type {
    captureException,
    captureMessage
} from '@sentry/browser';
import {
    useCustomAuth
} from '~/composables';
import {
    SessionId
} from '~/composables';
import {
    tryOnMounted,
    tryOnBeforeUnmount
} from '@vueuse/core';
import type {
    WatchStopHandle
} from 'vue';


export interface UseLoggingOptions {
    noAutoSync?: boolean;
}
export function useLogging(options: Partial<UseLoggingOptions> = {}) {
    let unWatchObj: WatchStopHandle;
    const { sessionObj } = useCustomAuth();
    const sessionId = SessionId();
    const autoSync = options.noAutoSync !== true;

    function syncSentryUser(): void {
        if (!import.meta.client || !window.Sentry?.setUser) return;

        if (!sessionObj.value) {
            window.Sentry.setUser({
                sessionId: sessionId.value
            });

            return;
        }

        const {
            email,
            uid: id,
            membershipChoices,
        } = sessionObj.value;

        const selectedMembershipUid = membershipChoices.find(o => o.isSelected)?.membershipUid ?? null;

        window.Sentry.setUser({
            id,
            email,
            selectedMembershipUid,
            sessionId: sessionId.value
        });
    }

    function clearSentryUser(): void {
        if (!import.meta.client || !window.Sentry?.setUser) return;
        window.Sentry.setUser(null);
    }

    function logSentryError(...args: Parameters<typeof captureException>): Undefinable<string> {
        if (import.meta.client) {
            return window.Sentry?.captureException(...args);
        }
    }

    function logSentryMessage(...args: Parameters<typeof captureMessage>): Undefinable<string> {
        if (import.meta.client) {
            return window.Sentry?.captureMessage(...args);
        }
    };

    if (import.meta.client && autoSync) {
        unWatchObj = watch(
            [
                () => sessionObj.value?.membershipChoices?.find(o => o.isSelected)?.membershipUid,
                () => sessionObj.value?.uid
            ],
            syncSentryUser
        );
    }

    tryOnMounted(() => {
        syncSentryUser();
    });

    tryOnBeforeUnmount(() => {
        unWatchObj?.();
    });

    return {
        logSentryError,
        logSentryMessage,
        syncSentryUser,
        clearSentryUser
    }
}
